import { useEffect, useRef, useState } from "react";

/**
 * MarqueeRow — infinite auto-scrolling row with buttery-smooth finger/mouse drag.
 * During drag we bypass React setState and manipulate transform directly via ref
 * inside requestAnimationFrame — no jank, no lag.
 *
 * Fix notes (see chat):
 * - A movement threshold (DRAG_THRESHOLD) now gates when we actually consider
 *   the gesture a "drag". A plain tap/click never crosses it, so `dragging`
 *   never flips to true, so `stopClickIfDragging` never blocks the click —
 *   links inside the marquee are clickable again.
 * - When a real drag ends, we clear the inline transform so the CSS
 *   @keyframes animation (marquee-left / marquee-right) can resume instead of
 *   staying frozen forever (inline style otherwise permanently overrides it).
 */
const DRAG_THRESHOLD = 6; // px of movement before we treat this as a drag, not a click

const MarqueeRow = ({ children, direction = "left", speed = 40, className = "", gap = 20, noPause = false }) => {
  const wrapRef = useRef(null);
  const trackRef = useRef(null);
  const drag = useRef({ x: 0, base: 0, active: false, offset: 0, raf: 0, moved: false });
  const [dragging, setDragging] = useState(false);

  const style = {
    "--speed": `${Math.max(20, 3000 / Math.max(1, speed))}s`,
    "--gap": `${gap}px`,
  };

  const applyTransform = () => {
    if (trackRef.current) {
      trackRef.current.style.transform = `translate3d(${drag.current.offset}px, 0, 0)`;
    }
    drag.current.raf = 0;
  };

  const schedule = () => {
    if (drag.current.raf) return;
    drag.current.raf = requestAnimationFrame(applyTransform);
  };

  const beginDrag = (clientX) => {
    if (!trackRef.current) return;
    const cs = window.getComputedStyle(trackRef.current);
    const matrix = new DOMMatrixReadOnly(cs.transform === "none" ? "" : cs.transform);
    drag.current.x = clientX;
    drag.current.base = matrix.m41;
    drag.current.offset = matrix.m41;
    drag.current.active = true;
    drag.current.moved = false;
    // Freeze animation at its current visual position, in case this turns into a real drag.
    trackRef.current.style.transform = `translate3d(${matrix.m41}px, 0, 0)`;
    // NOTE: setDragging(true) intentionally NOT called here — see moveDrag.
  };

  const moveDrag = (clientX) => {
    if (!drag.current.active) return;
    const delta = clientX - drag.current.x;

    if (!drag.current.moved && Math.abs(delta) > DRAG_THRESHOLD) {
      drag.current.moved = true;
      setDragging(true); // only now do we commit to "this is a drag, not a click"
    }

    if (drag.current.moved) {
      drag.current.offset = drag.current.base + delta;
      schedule();
    }
  };

  const endDrag = () => {
    if (!drag.current.active) return;
    drag.current.active = false;
    if (drag.current.raf) {
      cancelAnimationFrame(drag.current.raf);
      drag.current.raf = 0;
    }

    if (drag.current.moved) {
      // Real drag: briefly keep `dragging` true so the click-suppression
      // capture handler can still catch the resulting synthetic click,
      // then release the inline transform so CSS animation resumes.
      setTimeout(() => {
        setDragging(false);
        if (trackRef.current) {
          trackRef.current.style.transform = "";
        }
      }, 40);
    } else if (trackRef.current) {
      // Was never a drag (plain tap/click) — release the inline transform
      // immediately so the CSS animation keeps running uninterrupted.
      trackRef.current.style.transform = "";
    }
  };

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onTouchStart = (e) => beginDrag(e.touches[0].clientX);
    const onTouchMove = (e) => moveDrag(e.touches[0].clientX);
    const onTouchEnd = () => endDrag();
    const onMouseDown = (e) => { beginDrag(e.clientX); };
    const onMouseMove = (e) => moveDrag(e.clientX);
    const onMouseUp = () => endDrag();

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const stopClickIfDragging = (e) => {
    if (dragging) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <div
      ref={wrapRef}
      className={`marquee-wrap ${noPause ? "marquee-nopause" : ""} ${className} select-none`}
      style={{ ...style, cursor: "grab", touchAction: "pan-y", willChange: "transform" }}
      onClickCapture={stopClickIfDragging}
    >
      <div
        ref={trackRef}
        className={`marquee-track ${direction === "right" ? "marquee-right" : "marquee-left"} ${dragging ? "marquee-paused" : ""}`}
      >
        <div className="marquee-group">{children}</div>
        <div className="marquee-group" aria-hidden="true">{children}</div>
      </div>
    </div>
  );
};

export default MarqueeRow;
